import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CitePopup, wrapWithCitations } from '../UI/Citations';
import { FaBrain, FaSyncAlt, FaPlay, FaLightbulb, FaClock, FaCalendarAlt } from 'react-icons/fa';
import {
  TechSection,
  TechButton,
  StatusPill,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { getDeepSeekResponse } from '../../services/api/deepseek';
import { listInsights } from '../../services/researchService';
import { RESEARCH_ITEMS } from './data';

const Output = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 4px;
  padding: 1rem 1.15rem;
  color: var(--text-secondary);
  line-height: 1.55;
  font-size: 0.92rem;

  h2, h3, h4 { color: var(--text-primary); margin: 0.85em 0 0.35em; font-weight: 600; }
  h3 { font-size: 0.95em; text-transform: uppercase; letter-spacing: 0.1em; color: ${accent}; }
  p { margin: 0 0 0.7em; }
  ul, ol { margin: 0 0 0.7em 1.2em; padding: 0; }
  li { margin-bottom: 0.25em; }
  strong { color: var(--text-primary); }
  blockquote {
    border-left: 2px solid ${accent};
    padding-left: 0.85em;
    margin: 0.6em 0;
    color: var(--text-disabled);
  }
  code {
    font-family: ${mono};
    font-size: 0.88em;
    padding: 1px 5px;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.4);
    color: #cfe9f4;
  }
`;

const Empty = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.9rem;
`;

const Error = styled(Empty)`
  color: var(--alert-error);
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1.5rem;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: ${accent};
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
`;

const Footer = styled.div`
  margin-top: 0.7rem;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  display: flex;
  gap: 0.85rem;
  flex-wrap: wrap;

  span { display: inline-flex; align-items: center; gap: 5px; }
  svg { color: ${accent}; font-size: 0.78rem; }
`;

const ScheduleStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 0.85rem;
`;

const PRESETS = [
  {
    id: 'gaps',
    label: 'Brechas',
    icon: <FaLightbulb />,
    promptBuilder: (items) => `Sos analista de research político.

Identificá brechas y solapamientos en este catálogo. Markdown:

## Brechas detectadas
3-5 áreas o segmentos sin cobertura suficiente.

## Solapamientos
Estudios cuya temática se repite y podrían consolidarse.

## Recomendaciones
Lista numerada (3 ítems) de próximos estudios prioritarios + por qué.

Catálogo:
${JSON.stringify(items, null, 2)}

Sé conciso.`,
  },
  {
    id: 'synthesis',
    label: 'Síntesis estratégica',
    icon: <FaBrain />,
    promptBuilder: (items) => `Sos analista político senior. Devolvé en markdown:

## Insight principal
1-2 frases con la conclusión transversal más importante.

## Temas dominantes
Bullets con los 3-4 temas que más aparecen y qué dicen en conjunto.

## Acciones tácticas
Lista numerada (3-5) para la próxima semana.

## Riesgos a vigilar
2 riesgos.

Catálogo:
${JSON.stringify(items, null, 2)}

No repitas títulos literales.`,
  },
];

const fmtTs = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleString('es-AR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ResearchInsights = () => {
  const [active, setActive] = useState('synthesis');
  const [serverInsights, setServerInsights] = useState({});
  const [schedule, setSchedule] = useState(null);
  const [loadingServer, setLoadingServer] = useState(true);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [activeCite, setActiveCite] = useState(null);

  // Carga inicial: insights ya generados por el cron semanal
  useEffect(() => {
    let cancelled = false;
    listInsights()
      .then((data) => {
        if (cancelled) return;
        const map = {};
        for (const ins of data.insights || []) map[ins.kind] = ins;
        setServerInsights(map);
        setSchedule(data.schedule || null);
      })
      .catch((e) => { if (!cancelled) setErr(e?.message || null); })
      .finally(() => { if (!cancelled) setLoadingServer(false); });
    return () => { cancelled = true; };
  }, []);

  const regenerate = async () => {
    setBusy(true);
    setErr(null);
    try {
      const preset = PRESETS.find((p) => p.id === active);
      const lite = RESEARCH_ITEMS.map((it) => ({
        titulo: it.title,
        tipo: it.type,
        categoria: it.category,
        fecha: it.date,
        autor: it.author,
        tags: it.tags,
        resumen: it.excerpt,
      }));
      const text = await getDeepSeekResponse({
        prompt: preset.promptBuilder(lite),
        max_tokens: 1100,
        temperature: 0.55,
      });
      setOverrides((prev) => ({ ...prev, [active]: { body: text, generated_at: new Date().toISOString() } }));
    } catch (e) {
      setErr(e?.message || 'No se pudo regenerar');
    } finally {
      setBusy(false);
    }
  };

  const current = overrides[active] || serverInsights[active];
  const isOverride = Boolean(overrides[active]);

  return (
    <TechSection
      title="Insights IA · Research"
      action={
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <StatusPill>IA</StatusPill>
          <StatusPill>Auto · semanal</StatusPill>
          <TechButton type="button" onClick={regenerate} disabled={busy}>
            {current ? <FaSyncAlt /> : <FaPlay />}
            {busy ? 'Generando…' : current ? 'Regenerar ahora' : 'Generar'}
          </TechButton>
        </span>
      }
    >
      {schedule && (
        <ScheduleStrip>
          <StatusPill>
            <FaCalendarAlt style={{ marginRight: 4, color: accent }} />
            Cadencia <strong>{schedule.cadence || 'weekly'}</strong>
          </StatusPill>
          {schedule.next_run_at && (
            <StatusPill>
              <FaClock style={{ marginRight: 4, color: accent }} />
              Próxima auto-generación <strong>{fmtDate(schedule.next_run_at)}</strong>
            </StatusPill>
          )}
        </ScheduleStrip>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '0.85rem' }}>
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActive(p.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '0.4rem 0.7rem',
              border: '1px solid',
              borderColor: active === p.id ? 'rgba(0, 180, 216, 0.5)' : borderSoft,
              background: active === p.id ? 'rgba(0, 180, 216, 0.1)' : panelDeep,
              color: active === p.id ? accent : 'var(--text-secondary)',
              borderRadius: 5,
              fontFamily: mono,
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {p.icon} {p.label}
          </button>
        ))}
      </div>

      {loadingServer && <Loading>Cargando últimos insights</Loading>}
      {err && !current && <Error>No se pudo cargar · {err}</Error>}
      {!loadingServer && !current && !err && (
        <Empty>
          Aún no hay insights generados. Tocá <strong>Generar</strong> o esperá a la próxima ventana auto-semanal.
        </Empty>
      )}

      {busy && <Loading>Procesando catálogo</Loading>}

      {!busy && current && (
        <>
          <Output>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={wrapWithCitations((cite) => setActiveCite(cite))}
            >
              {current.body}
            </ReactMarkdown>
          </Output>
          <Footer>
            <span><FaClock /> Generado · {fmtTs(current.generated_at)}</span>
            <span>{isOverride ? 'Manual (sobrescribe la versión auto)' : 'Auto-generado por el cron'}</span>
          </Footer>
        </>
      )}
      {activeCite && (
        <CitePopup
          cite={activeCite}
          indexed={Array.isArray(current?.citations) ? current.citations : []}
          fresh={[]}
          rag={[]}
          onClose={() => setActiveCite(null)}
        />
      )}
    </TechSection>
  );
};

export default ResearchInsights;
